import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { SheetItem, SheetLayerDef } from '../../doc/schema';
import { useAssetUrl } from '../assets/localAssets';
import { pickVariant, type VariantName } from '../assets/variants';
import { useBoard, usePane } from '../context';
import { rasterStore, type LiveLayer } from '../paint/rasterStore';
import { sheetLayerList } from '../paint/session';

/**
 * A sketch sheet: the paper, then every visible layer in order. A layer is an <img> of its
 * asset (variant by on-screen size, like images), or the live canvas of the raster store while
 * it is being painted. The canvas elements belong to the store, so they keep their pixels when
 * the item scrolls out of view and unmounts.
 */
export function SheetBody({ item, width }: { item: SheetItem; width: number }) {
  const board = useBoard();
  const store = rasterStore(board);
  useSyncExternalStore(store.subscribe, store.getVersion);
  const zoom = usePane((s) => s.settledZoom);
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const { pxW, pxH, paper } = item.props;
  const longest = Math.max(width, (width * pxH) / Math.max(1, pxW));
  const variant = pickVariant(longest * zoom * dpr);
  // two screen pixels or more per sheet pixel: show the pixels as they are, not smoothed
  const pixelated = (width * zoom * dpr) / Math.max(1, pxW) >= 2;
  return (
    <div className={`sheet-body${paper ? '' : ' is-transparent'}${pixelated ? ' is-pixelated' : ''}`} style={paper ? { background: paper } : undefined}>
      {sheetLayerList(item).map(([id, def]) =>
        def.visible ? <SheetLayerView key={id} def={def} live={store.liveLayer(item.id, id)} variant={variant} /> : null,
      )}
    </div>
  );
}

function SheetLayerView({ def, live, variant }: { def: SheetLayerDef; live: LiveLayer | undefined; variant: VariantName }) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = live?.canvas;
  const scratch = live?.scratch ?? null;

  // the store's canvases are appended here (React never owns them)
  useEffect(() => {
    const el = host.current;
    if (!el || !canvas) return;
    el.appendChild(canvas);
    if (scratch) el.appendChild(scratch);
    return () => {
      if (canvas.parentElement === el) el.removeChild(canvas);
      if (scratch && scratch.parentElement === el) el.removeChild(scratch);
    };
  }, [canvas, scratch]);

  const showImg = !!def.assetId && (!live || !live.loaded);
  return (
    <div className="sheet-layer" style={{ opacity: def.opacity }}>
      {showImg && <SheetLayerImage assetId={def.assetId!} variant={variant} />}
      {canvas && <div ref={host} className="sheet-live" />}
    </div>
  );
}

/** The layer's asset, decoded before it is shown so a variant swap never flashes. */
function SheetLayerImage({ assetId, variant }: { assetId: string; variant: VariantName }) {
  const url = useAssetUrl(assetId, variant);
  const [shown, setShown] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    const done = () => {
      if (!cancelled) setShown(url);
    };
    if (typeof img.decode === 'function') img.decode().then(done, done);
    else {
      img.onload = done;
      img.onerror = done;
    }
    return () => {
      cancelled = true;
    };
  }, [url]);
  if (!shown) return null;
  return <img className="sheet-layer-img" src={shown} alt="" draggable={false} decoding="async" />;
}
